import Card from "@mui/material/Card";
import { FS } from "@plannel/utils/font-util";

/*
 * 공용 카드 프리미티브 — MUI Card 의 룩을 앱 전역에서 통일한다(ai 디렉터리 제외).
 *
 * MUI 기본 Card 는 테두리 없이 드롭 그림자 + 각진 모서리라 화면마다 제각각으로 보였다.
 * 여기서 플랫 룩(옅은 divider 테두리 + 라운드 + 미세 그림자)을 기본값으로 준다.
 * 값은 DW 대시보드의 DashboardCard 와 맞춰, 두 카드를 나란히 둬도 어긋나지 않게 한다.
 *
 * drop-in 교체:  import Card from "@mui/material/Card"  →  import AppCard from "@plannel/components/ui/AppCard"
 *   · CardContent / CardMedia / CardActions 자식은 그대로 동작한다(내부가 MUI Card 이므로).
 *   · variant="outlined" 는 줄 필요 없다 — 자체 테두리가 이미 있다.
 *   · overflow: hidden 이라 라운드가 자식(split-panel 색 CardMedia 반쪽 등)을 따라 둥글게 깎는다.
 *   · elevation 은 0 이 기본, 그림자는 sx.boxShadow 로만. 개별 카드의 sx(width/height/margin/bgcolor 등)는
 *     뒤에서 병합되어 그대로 우선한다.
 */
/*
 * 카드 제목(헤더)의 표준 스타일 — 14px / 600 / text.primary.
 *   (예: DP Process Management "DP Version Information", BF Review "Baseline Forecast Run Time")
 *
 * 카드 콘텐츠를 다듬을 때(페이지별로 진행) 모든 카드 제목에 이 하나를 쓴다 — 스탯 카드든
 * 여러 줄 상세형 카드든 제목 톤을 통일한다(화면끼리 제목이 제각각으로 보이지 않게).
 * 제목이 카드를 이끄는 헤더가 되도록, 내용(값·상세)은 제목보다 작고 물러나게 둔다.
 *   <Typography sx={CARD_HEADER_SX}>{title}</Typography>                                        ← 제목(진한 헤더)
 *   <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>{detail}</Typography>  ← 상세(회색)
 *   ※ 강조할 값 하나(예: BF Review 의 "WAPE")는 제목보다 크게(22/500) 두어 주인공으로.
 *
 * ※ 대문자·넓은 자간은 쓰지 않는다 — 페이지의 다른 텍스트(같은 Noto Sans KR)와 톤이 튀지 않게.
 */
export const CARD_HEADER_SX = {
  fontSize: FS.TITLE,
  fontWeight: 600,
  color: "text.primary",
};

/*
 * 섹션 제목(페이지 안의 구역 이름)의 표준 스타일 — 16px / 600 / text.primary.
 *   (예: BF Review 의 "Forecast Metrics Result", "Forecast Algorithm Result")
 *
 * MUI variant="h6"(20px)은 다른 텍스트를 다 부드럽게 다듬은 화면에서 혼자 튄다.
 * 구역을 나눌 만큼의 무게는 주되 소리는 낮춘다. (전역 CSS 가 ≤1400px 에서 h6 를 14px 로 강제하므로
 *  variant="h6" 를 유지한 채 이 sx 로 넓은 화면의 20px 만 눌러도 좁은 화면과 크게 어긋나지 않는다.)
 */
/*
 * 섹션 제목은 카드 제목(TITLE)과 **한 칸 건너뛴 H1** 이다.
 *   인접 역할(TITLE↔H2)은 1px 차이라 같은 화면에서 위계가 안 보인다 — 타이포 표준 §2 참고.
 */
export const SECTION_TITLE_SX = {
  fontSize: FS.H1,
  fontWeight: 600,
  color: "text.primary",
};

const AppCard = ({ children, sx, elevation = 0, ...others }) => (
  <Card
    elevation={elevation}
    sx={{
      // 앱에 전역 CssBaseline(box-sizing 리셋)이 없어 기본이 content-box 다.
      // AppCard 는 항상 테두리(1px)를 갖고, 종종 height:100% + 패딩과 함께 쓰이므로
      // border-box 로 고정해 (테두리·패딩이 높이에 포함되게) 부모를 넘치지 않게 한다.
      boxSizing: "border-box",
      border: 1,
      borderColor: "divider",
      borderRadius: 1.5,
      boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
      overflow: "hidden",
      ...sx,
    }}
    {...others}
  >
    {children}
  </Card>
);

export default AppCard;
